import { hideSplashScreen } from "@src/appV2/NativeAdapters/splashScreen.adapter";
import { Box } from "@mui/material";
import { type SyntheticEvent, useCallback, useEffect, useRef, useState } from "react";

import { SplashContent } from "./SplashContent";

const FADE_OUT_MS = 400;
const APP_READY_POLL_MS = 200;

declare global {
  interface Window {
    __APP_READY__?: boolean;
  }
}

interface MobileSplashOverlayProps {
  onFinished: () => void;
}

export function MobileSplashOverlay({ onFinished }: MobileSplashOverlayProps) {
  const [isFadingOut, setIsFadingOut] = useState(false);
  const dismissedRef = useRef(false);
  const nativeSplashHiddenRef = useRef(false);
  const onFinishedRef = useRef(onFinished);
  onFinishedRef.current = onFinished;

  const hideNativeSplashOnce = useCallback(() => {
    if (nativeSplashHiddenRef.current) {
      return;
    }
    nativeSplashHiddenRef.current = true;
    hideSplashScreen({ fadeOutDuration: 0 }).catch((error: unknown) => {
      // eslint-disable-next-line no-console
      console.warn("Failed to hide splash screen:", error);
    });
  }, []);

  // Dismiss as soon as window.__APP_READY__ fires (set by AppReadySignal in
  // src/index.tsx after the MUI / LD / Rebrand provider tree mounts). The
  // top-level Suspense fallback also renders SplashContent, so when this
  // overlay fades out the user sees an identical splash underneath until
  // the lazy chunks resolve — no visible double-spinner.
  useEffect(() => {
    if (window.__APP_READY__) {
      if (!dismissedRef.current) {
        dismissedRef.current = true;
        setIsFadingOut(true);
        setTimeout(() => onFinishedRef.current(), FADE_OUT_MS);
      }
      return undefined;
    }

    const pollInterval = setInterval(() => {
      if (window.__APP_READY__ && !dismissedRef.current) {
        clearInterval(pollInterval);
        dismissedRef.current = true;
        setIsFadingOut(true);
        setTimeout(() => onFinishedRef.current(), FADE_OUT_MS);
      }
    }, APP_READY_POLL_MS);

    return () => {
      clearInterval(pollInterval);
    };
  }, []);

  const handleImageLoaded = useCallback(
    async (event: SyntheticEvent<HTMLImageElement>) => {
      // onLoad fires when the bitmap is fetched but decoding may still be in
      // flight. Await decode() and double-rAF so the painted pixels hit the
      // screen before the native splash dismisses, avoiding a flash-off frame.
      const img = event.currentTarget;
      try {
        await img.decode();
      } catch {
        // decode failed — dismiss anyway so the user isn't stuck on the native splash.
      }
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          hideNativeSplashOnce();
        });
      });
    },
    [hideNativeSplashOnce]
  );

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        opacity: isFadingOut ? 0 : 1,
        transition: `opacity ${FADE_OUT_MS}ms ease-out`,
        pointerEvents: isFadingOut ? "none" : "auto",
      }}
    >
      <SplashContent onImageLoad={handleImageLoaded} onImageError={hideNativeSplashOnce} />
    </Box>
  );
}
